import { Rank, type EnemyHookContext } from '../../sim/defs';
import { Fam } from '../../sim/pools/BulletPool';
import { carrierX, COMBAT_START, CYCLE, firstDelayAt, PHASE_NOYAU } from './navette.cycle';
import { defineEnemy, Motion, Pattern } from '../schema';

/* ═══════════════════════════════════════════════════════════════════════════
   B02 — LA NAVETTE. Boss du monde 2.

   ── Ce qu'elle enseigne ──
   Le contraire du Héraut, et c'est tout l'intérêt de la placer en deuxième.

   Le Héraut vient au joueur et se laisse traîner : la leçon est « je le
   pilote », et le réflexe qu'elle installe est de rester sous le boss. La
   Navette ignore le joueur — elle traverse le terrain d'un bord à l'autre, à
   vitesse constante, et s'ancre à l'extrémité atteinte pour y tirer sa salve
   visée. Le joueur qui applique le réflexe du monde 1 la suit jusqu'au bord et
   reçoit la salve à bout portant, coincé dans un coin.

   La réponse est de croiser le boss à contresens pendant la traversée et
   d'attendre l'ancrage à l'autre extrémité. Aucun texte ne l'explique ; la
   traversée dure cinq secondes, ce qui laisse largement le temps de voir où
   elle va, et l'arrêt annonce la salve avec 0,9 s d'avance.

   ── Une silhouette plus simple que le Héraut ──
   Quatre parties au lieu de six, et volontairement. La difficulté du monde 2 ne
   vient pas du nombre d'armes mais du MOUVEMENT : ajouter des parties aurait
   dilué la lecture de la traversée, qui est la seule chose à comprendre ici.

   ── Points de vie ──
   Les chiffres déclarés sont ceux de la BASE ; le monde 2 applique ×1,6.

   Ils ne sont pas choisis à l'estime mais DÉDUITS de la courbe de puissance
   (src/game/curve.ts), au niveau RÉELLEMENT atteint ici.

   ⚠️ Ce niveau est 11, et non 14 comme la lecture naïve de la courbe le
   suggérait. `tests/balance/campagne.test.ts` déroule les trois mondes et
   mesure : 7 au premier boss, 11 au deuxième, 20 au troisième. La première
   version de ce fichier supposait 14 — soit 1 250 DPS au lieu de 815 — et
   donnait un combat de 77 s au lieu de 50. L'écart ne se voyait nulle part :
   chaque fichier était cohérent avec lui-même, et l'hypothèse fausse vivait
   dans le commentaire d'un troisième.

   25 600 × 1,6 = 40 960 PV effectifs à 815 DPS, soit ~50 s — la même durée que
   le Héraut, à un moment où le joueur frappe près de deux fois plus fort. C'est
   ce que veut dire « le coefficient de monde est compensé par la courbe » : la
   difficulté ne monte pas, elle se maintient.

   Le levier d'ajustement est ce coefficient ou ces valeurs — jamais l'ajout
   d'une arme au noyau, qui détruirait la fenêtre de punition.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Le hook de traversée.
 *
 *  Échappatoire documentée (PLAN.md §3.3), justifiée par la même raison que
 *  chez le Héraut : aucune des quatre trajectoires du moteur ne sait « traverser
 *  en cinq secondes, s'arrêter net, repartir en sens inverse au cycle suivant ».
 *  `Motion.Strafe` traverse mais ne s'arrête ni ne revient — un boss en Strafe
 *  sortirait simplement du cadre et se ferait éliminer par le culling.
 *
 *  Il obéit aux règles de src/sim/ : pur, sans allocation, sans DOM, sans Math
 *  transcendante.
 *
 *  ⚠️ Le hook ne touche QUE l'abscisse. Le porteur se poste à Y = +345 par sa
 *  trajectoire Hover et n'en bouge plus, ce qui garantit STRUCTURELLEMENT
 *  l'invariant de PLAN.md §6.6 : aucune émission sous Y = -180. La partie la
 *  plus basse est un peigne à offY -60, soit Y = +285. */
function traverser(slot: number, age: number, dt: number, ctx: EnemyHookContext): void {
  // Entrée en scène : descente droite dans l'axe, sans traversée. Le joueur
  // doit d'abord voir la silhouette au repos, au milieu, avant qu'elle ne se
  // mette à balayer — sinon l'arrivée est illisible.
  const t = age - COMBAT_START;
  if (t < 0) return;
  void dt; // la position est calculée depuis l'âge, jamais intégrée
  ctx.x[slot] = carrierX(t);
}

export default defineEnemy({
  id: 'navette',
  rank: Rank.Heavy,
  boss: true,
  hp: 18000,
  visualRadius: 92,
  score: 19000,
  /* 2,2 s de télégraphe, comme le Héraut : intouchable ET inoffensive, parties
     comprises, qui en héritent. Le joueur doit pouvoir compter les peignes
     avant de pouvoir tirer dessus. */
  telegraph: 2.2,
  drops: 16,
  /* Hover jusqu'à Y = +345 puis station. Amplitude et fréquence à zéro :
     l'oscillation du moteur entrerait en concurrence avec la traversée du hook,
     et le joueur ne saurait plus lire où va le boss — or c'est exactement ce
     qu'on lui demande d'anticiper. */
  motion: { kind: Motion.Hover, speed: 180, amplitude: 0, frequency: 0, holdY: 345 },
  emitter: {
    /* Éventail STATIQUE : le noyau ne vise jamais. Toute la menace dirigée passe
       par les peignes et disparaît avec eux — même règle que chez le Héraut, et
       même conséquence : la phase finale reste un combat, pas un sac de PV. */
    pattern: Pattern.Fan,
    every: CYCLE,
    count: 13,
    // 172° à 300 u/s : un dôme lent qu'on traverse en marchant, à condition de
    // ne pas être resté sous le porteur.
    spreadDeg: 172,
    speed: 300,
    firstDelay: firstDelayAt(PHASE_NOYAU),
    family: Fam.EnemyRed,
  },

  /* ── Le finale, en deux phases ─────────────────────────────────────────────
     `shieldedByParts` : le noyau ne se blesse qu'une fois les peignes et fuseaux
     tombés. Le finale, débarrassé des armes, exploite ce que la Navette fait de
     mieux — TRAVERSER : un FAISCEAU balayant qui suit le porteur d'un bord à
     l'autre (le Beam est fait pour ça), puis une RAFALE visée en enrage. Une
     seule source (cap monde 2 à 130), largement sous le plafond.
     ⚠️ `beamLen` 360 depuis la station à +345 → bas du faisceau à −15, au-dessus
     de la ligne −180 : aucun `beamWarn` nécessaire. `beamLife` < `every`. */
  phases: [
    {
      // DERNIÈRE ARME TOMBÉE (4 parties détruites) : dès l'exposition, le faisceau.
      // Il balaie l'écran au rythme de la traversée — pas d'éventail mort au début.
      atPartsLost: 4,
      telegraph: 0.8,
      speedMul: 1.1,
      emitter: {
        pattern: Pattern.Beam,
        every: 2.0,
        count: 19,
        beamLen: 360,
        beamLife: 1.2,
        family: Fam.EnemyRed,
      },
    },
    {
      // 33 % : la rafale visée. Une giclée, puis une pause — densité auto-bornée.
      atHpFrac: 0.33,
      telegraph: 1.0,
      speedMul: 1.25,
      emitter: {
        pattern: Pattern.Burst,
        every: 2.2,
        count: 6,
        spreadDeg: 26,
        speed: 340,
        burstGap: 0.1,
      },
    },
  ],
  onUpdate: traverser,

  /* ⚠️ Les ordonnées. Le point le plus bas est un peigne à offY -60, soit
     Y = +285 une fois le porteur posé. L'invariant « aucun tir sous Y = -180 »
     tient avec 465 u de marge, et il tient STRUCTURELLEMENT : le porteur ne
     descend plus après sa station, et le hook ne touche pas à Y. */
  parts: [
    { enemy: 'navette-peigne', offX: -180, offY: -60 },
    { enemy: 'navette-peigne', offX: 180, offY: -60 },
    { enemy: 'navette-fuseau', offX: -76, offY: 30 },
    { enemy: 'navette-fuseau', offX: 76, offY: 30 },
  ],

  /* Noyau intouchable tant qu'une partie survit. Le joueur choisit son ordre,
     donc sa difficulté :

       fuseaux d'abord   1 600 PV, rapide, mais on garde la salve visée sur le
                         dos pendant tout le reste du combat.
       peignes d'abord   6 000 PV sous un quadrillage d'anneaux qui ne faiblit
                         pas — près de quatre fois plus long, mais le combat
                         devient ensuite entièrement lisible.

     Les valeurs sont de base ; le monde 2 les multiplie par 1,6.

     Et la dégradation va dans le bon sens : si le pool sature et qu'aucune
     partie n'apparaît, `livingParts` renvoie zéro et le noyau redevient
     vulnérable. Un boss diminué, jamais un boss invincible. */
  shieldedByParts: true,
});
